/**
 * Tiny best-effort retry queue for content-free calls the UI must never wait
 * on (capture events, a withdraw that hit a dead connection). Tasks run in
 * order; a retryable failure backs off and tries again, a permanent one is
 * dropped. In memory only — the server's own state machine is the backstop.
 */

export interface RetryTask {
  /** Stable label for de-duplication / debugging (never user content). */
  key: string
  run: () => Promise<unknown>
}

export interface RetryQueue {
  push(task: RetryTask): void
  /** Pending tasks (tests / diagnostics). */
  size(): number
  /** Try now, skipping any backoff (e.g. coming back online). */
  flush(): void
  clear(): void
}

const DEFAULT_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000, 60_000]

export function createRetryQueue(options: {
  isRetryable: (err: unknown) => boolean
  backoffMs?: readonly number[]
  maxAttempts?: number
  schedule?: (fn: () => void, ms: number) => unknown
  cancel?: (handle: unknown) => void
}): RetryQueue {
  const backoff = options.backoffMs ?? DEFAULT_BACKOFF_MS
  const maxAttempts = options.maxAttempts ?? backoff.length + 1
  const schedule = options.schedule ?? ((fn, ms) => setTimeout(fn, ms))
  const cancel = options.cancel ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>))
  const queue: Array<{ task: RetryTask; attempts: number }> = []
  let running = false
  let timer: unknown = null

  const clearTimer = () => {
    if (timer != null) cancel(timer)
    timer = null
  }

  const drain = async () => {
    if (running) return
    running = true
    clearTimer()
    try {
      while (queue.length > 0) {
        const head = queue[0]
        try {
          await head.task.run()
          queue.shift()
        } catch (err) {
          head.attempts += 1
          if (!options.isRetryable(err) || head.attempts >= maxAttempts) {
            queue.shift()
            continue
          }
          const delay = backoff[Math.min(head.attempts - 1, backoff.length - 1)] ?? 60_000
          timer = schedule(() => {
            timer = null
            void drain()
          }, delay)
          return
        }
      }
    } finally {
      running = false
    }
  }

  return {
    push(task) {
      queue.push({ task, attempts: 0 })
      if (timer == null) void drain()
    },
    size: () => queue.length,
    flush() {
      if (queue.length > 0) void drain()
    },
    clear() {
      clearTimer()
      queue.length = 0
    },
  }
}
